import { useEffect, useState, useRef, useCallback } from "react";

const fontStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;500;700;900&family=Montserrat:wght@200;400;700&display=swap');
`;

const MobileElevatorAnimation = ({ onAnimationComplete }) => {
  const [doorState, setDoorState] = useState("closed");
  const [floorCount, setFloorCount] = useState(1);
  const [isStarted, setIsStarted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [lightsOn, setLightsOn] = useState(false);
  const [particles, setParticles] = useState([]);

  const particleRef = useRef(null);
  const audioRef = useRef(null);
  const doorSoundRef = useRef(null);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isMountedRef = useRef(true);

  // Generate particles for the intro screen - simplified for mobile
  useEffect(() => {
    // Check if component is mounted
    if (!isStarted && particleRef.current) {
      // Create particles with safe dimensions
      const containerWidth =
        particleRef.current?.offsetWidth || window.innerWidth;
      const containerHeight =
        particleRef.current?.offsetHeight || window.innerHeight;

      const newParticles = [];
      // Generate fewer particles for mobile
      for (let i = 0; i < 15; i++) {
        newParticles.push({
          x: Math.random() * containerWidth,
          y: Math.random() * containerHeight,
          size: Math.random() * 2 + 1,
          speedX: Math.random() * 0.8 - 0.4,
          speedY: Math.random() * 0.8 - 0.4,
          opacity: Math.random() * 0.5 + 0.3,
        });
      }
      setParticles(newParticles);

      const animateParticles = () => {
        if (!isMountedRef.current || !particleRef.current) return;

        setParticles((prevParticles) =>
          prevParticles.map((p) => {
            // Get current dimensions safely
            const width = particleRef.current?.offsetWidth || window.innerWidth;
            const height =
              particleRef.current?.offsetHeight || window.innerHeight;

            return {
              ...p,
              x: (p.x + p.speedX + width) % width,
              y: (p.y + p.speedY + height) % height,
            };
          })
        );

        if (!isStarted && isMountedRef.current) {
          animationFrameRef.current = requestAnimationFrame(animateParticles);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animateParticles);
    }

    // Cleanup animation on unmount
    return () => {
      isMountedRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isStarted]);

  // Run animation sequence after user interaction - optimized for mobile
  const startAnimation = () => {
    setIsStarted(true);
    setIsAnimating(true);

    // Create a user interaction context for audio
    const unlockAudio = () => {
      // Create context for audio playback
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      // Resume the audio context
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }

      // Pre-load the audio files
      if (audioRef.current) {
        audioRef.current.load();
      }
      if (doorSoundRef.current) {
        doorSoundRef.current.load();
      }

      // Create and play a silent sound to unlock audio
      const silentSound = new Audio(
        "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABQABgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBg//8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAAAAAAAAAAAQOsDn1xxAAAAAAAAAAAAAAAAAAAAAP/7kGQAAAAAADkAAAAIAAANIAAAAQAAAOQAAAAgAAA0gAAABExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV"
      );

      // Play with both methods to maximize compatibility
      const playPromise = silentSound.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log(
            "Silent sound failed, but we'll try again with the real sounds"
          );
        });
      }
    };

    unlockAudio();
  };

  const playSound = useCallback((audioElement) => {
    if (!audioElement) return;
    audioElement.volume = 1.0;
    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.log("Sound play error:", error);
      });
    }
  }, []);

  // Simplified keyframes for mobile
  const keyframesStyle = `
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes buttonGlow {
        0% {
            box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
        }
        50% {
            box-shadow: 0 0 12px rgba(0, 123, 255, 0.7);
        }
        100% {
            box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
        }
    }
    
    @keyframes floorNumberChange {
        0% {
            transform: scale(1.1);
            opacity: 0.8;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }

    @keyframes doorShine {
        0% {
            background-position: -100% 0;
        }
        100% {
            background-position: 200% 0;
        }
    }
    
    .welcome-heading {
        animation: fadeInUp 0.8s ease-out forwards;
    }
    
    .welcome-text {
        animation: fadeInUp 0.8s ease-out 0.2s forwards;
        opacity: 0;
    }
    
    .start-button {
        animation: fadeInUp 0.8s ease-out 0.4s forwards, buttonGlow 3s infinite 1s;
        opacity: 0;
    }
    
    .floor-button.active {
        animation: buttonGlow 1.5s infinite;
    }
    
    .floor-number {
        animation: floorNumberChange 0.3s ease-out;
    }
  `;

  // Animation sequence optimized for mobile
  useEffect(() => {
    if (!isStarted) return;

    const sequence = async () => {
      try {
        // Turn on elevator lights
        setLightsOn(true);

        // Initial floor counting animation
        for (let i = 1; i <= 5; i++) {
          setFloorCount(i);
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Play door opening sound
        if (doorSoundRef.current) {
          playSound(doorSoundRef.current);
        }

        // Start opening doors
        setDoorState("opening");

        // Doors fully open
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setDoorState("open");

        // Play elevator ding sound
        if (audioRef.current) {
          playSound(audioRef.current);
        }

        // Short pause before transition
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Transition to home page
        if (containerRef.current) {
          containerRef.current.style.opacity = 0;
        }
        setTimeout(() => {
          onAnimationComplete();
        }, 600);
      } catch (error) {
        console.error("Animation sequence error:", error);
        onAnimationComplete();
      }
    };

    sequence();
  }, [isStarted, onAnimationComplete, playSound]);

  return (
    <>
      <style>{keyframesStyle}</style>
      <style>{fontStyles}</style>

      <div ref={containerRef} style={styles.container}>
        {!isStarted ? (
          <div
            ref={particleRef}
            className="welcome-container"
            style={styles.startButtonContainer}
          >
            {/* Render simplified particles */}
            {particles.map((particle, index) => (
              <div
                key={index}
                style={{
                  position: "absolute",
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  left: `${particle.x}px`,
                  top: `${particle.y}px`,
                  opacity: particle.opacity,
                  boxShadow: "0 0 5px rgba(0, 123, 255, 0.5)",
                }}
              />
            ))}

            <div style={styles.welcomeContent}>
              <div style={styles.topAccentLine}></div>
              {/* <div style={styles.logoMark}>DC</div> */}
              <h2 className="welcome-heading" style={styles.welcomeHeading}>
                INTELLIGENT ELEVATOR{" "}
                <span style={styles.accentText}>SYSTEMS</span>
              </h2>
              <p className="welcome-text" style={styles.welcomeText}>
                Advanced technology with precision engineering for superior
                vertical mobility
              </p>
              <button
                className="start-button"
                style={styles.startButton}
                onClick={startAnimation}
              >
                <span style={styles.buttonIcon}>⟁</span> Launch
              </button>
              <div style={styles.welcomeFooter}>
                <span style={styles.footerText}>DEECEE ELEVATORS</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.elevatorFrame}>
            {/* Elevator frame */}
            <div style={styles.elevatorCasing}>
              <div
                style={{
                  ...styles.ceilingLight,
                  opacity: lightsOn ? 1 : 0.2,
                }}
              ></div>
            </div>

            {/* Floor indicator */}
            <div style={styles.digitalFloorIndicator}>
              <div style={styles.floorIndicatorDisplay}>
                <div style={styles.floorDirectionIndicator}>
                  <span style={styles.directionArrowUp}>▲</span>
                </div>
                <div style={styles.floorNumberContainer}>
                  <div style={styles.floorNumber}>{floorCount}</div>
                </div>
                <div style={styles.floorText}>FLOOR</div>
              </div>
            </div>

            {/* Elevator interior */}
            <div style={styles.elevatorInterior}>
              <div style={styles.interiorWall}></div>

              {/* Digital wall display */}
              <div style={styles.digitalDisplay}>
                <div style={styles.displayHeader}>WELCOME TO</div>
                <div style={styles.displayContent}>DEECEE ELEVATORS</div>
              </div>

              {/* Enhanced floor */}
              <div style={styles.elevatorFloor}></div>
            </div>

            {/* Control panel */}
            <div style={styles.controlPanel}>
              <div style={styles.panelScreen}>
                <span style={styles.screenText}>FLOOR</span>
                <span style={styles.screenNumber}>{floorCount}</span>
              </div>

              {[1, 2, 3, 4, 5].map((num) => (
                <div
                  key={num}
                  className={
                    floorCount >= num ? "floor-button active" : "floor-button"
                  }
                  style={{
                    ...styles.floorButton,
                    backgroundColor: floorCount >= num ? "#0078ff" : "#2a2a2a",
                  }}
                >
                  {num}
                </div>
              ))}
            </div>

            {/* Left door */}
            <div
              style={{
                ...styles.door,
                ...styles.leftDoor,
                transform:
                  doorState === "closed"
                    ? "translateX(0)"
                    : doorState === "opening" || doorState === "open"
                    ? "translateX(-100%)"
                    : "translateX(0)",
              }}
            >
              <div style={styles.doorHandleLeft}></div>
            </div>

            {/* Right door */}
            <div
              style={{
                ...styles.door,
                ...styles.rightDoor,
                transform:
                  doorState === "closed"
                    ? "translateX(0)"
                    : doorState === "opening" || doorState === "open"
                    ? "translateX(100%)"
                    : "translateX(0)",
              }}
            >
              <div style={styles.doorHandleRight}></div>
            </div>

            {/* Elevator lights */}
            <div
              style={{
                ...styles.elevatorLight,
                opacity: lightsOn ? 0.9 : 0.1,
              }}
            ></div>
          </div>
        )}

        {/* Audio elements */}
        <audio
          ref={audioRef}
          src="/sounds/elevator-ting.mp3"
          preload="auto"
          controls={false}
          crossOrigin="anonymous"
        />
        <audio
          ref={doorSoundRef}
          src="/sounds/door-open.mp3"
          preload="auto"
          controls={false}
          crossOrigin="anonymous"
        />
      </div>
    </>
  );
};

// Mobile-optimized styles
const styles = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#0a0a0a",
    zIndex: 1000,
    transition: "opacity 1s ease",
  },
  startButtonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
    background:
      "linear-gradient(135deg, #0a1018 0%, #0f1d35 50%, #162b50 100%)",
    position: "relative",
    overflow: "hidden",
  },
  welcomeContent: {
    textAlign: "center",
    padding: "40px 25px",
    borderRadius: "8px",
    backdropFilter: "blur(8px)",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    boxShadow:
      "0 20px 40px rgba(0,0,0,0.2), inset 0 1px 1px rgba(0, 123, 255, 0.05)",
    border: "1px solid rgba(0, 123, 255, 0.1)",
    maxWidth: "300px",
    position: "relative",
    overflow: "hidden",
    zIndex: 10,
  },
  topAccentLine: {
    position: "absolute",
    top: 0,
    left: "10%",
    right: "10%",
    height: "2px",
    background:
      "linear-gradient(to right, rgba(0, 123, 255, 0), rgba(0, 123, 255, 0.5), rgba(0, 123, 255, 0))",
    boxShadow: "0 0 10px rgba(0, 123, 255, 0.5)",
  },
  logoMark: {
    position: "absolute",
    top: "15px",
    left: "15px",
    fontSize: "20px",
    fontFamily: "'Montserrat', sans-serif",
    color: "rgba(0, 123, 255, 0.8)",
    fontWeight: "700",
    letterSpacing: "1px",
  },
  welcomeHeading: {
    color: "#ffffff",
    fontSize: "1.8rem",
    marginBottom: "20px",
    fontWeight: "700",
    letterSpacing: "1px",
    textTransform: "uppercase",
    fontFamily: "'Montserrat', sans-serif",
    lineHeight: "1.2",
    position: "relative",
    zIndex: 2,
  },
  accentText: {
    color: "#0078ff",
    display: "block",
    marginTop: "2px",
    fontWeight: "700",
  },
  welcomeText: {
    color: "#c4d8f0",
    fontSize: "1rem",
    marginBottom: "30px",
    lineHeight: "1.5",
    fontWeight: "300",
    maxWidth: "100%",
    margin: "0 auto 30px auto",
    fontFamily: "'Poppins', sans-serif",
  },
  startButton: {
    padding: "15px 30px",
    fontSize: "1rem",
    background:
      "linear-gradient(to right, rgba(0, 89, 255, 0.9), rgba(0, 123, 255, 0.9))",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    boxShadow: "0 8px 15px rgba(0, 123, 255, 0.3)",
    transition: "all 0.3s ease",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    fontFamily: "'Montserrat', sans-serif",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  buttonIcon: {
    marginRight: "8px",
    fontSize: "1.2rem",
    fontWeight: "bold",
  },
  welcomeFooter: {
    marginTop: "30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "rgba(255,255,255,0.5)",
    fontSize: "0.8rem",
    letterSpacing: "1px",
    fontFamily: "'Montserrat', sans-serif",
  },
  footerText: {
    fontWeight: "300",
  },

  // Elevator elements
  elevatorFrame: {
    position: "relative",
    width: "100%",
    height: "100vh",
    backgroundColor: "#0a0a0a",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  elevatorCasing: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#0a0a0a",
    zIndex: 0,
    overflow: "hidden",
  },
  ceilingLight: {
    position: "absolute",
    top: "5%",
    left: "50%",
    width: "60%",
    height: "8px",
    backgroundColor: "rgba(0, 123, 255, 0.1)",
    transform: "translateX(-50%)",
    borderRadius: "10px",
    transition: "all 0.8s ease",
    zIndex: 2,
    boxShadow:
      "0 0 40px 8px rgba(0, 123, 255, 0.4), inset 0 0 20px rgba(255, 255, 255, 0.3)",
  },

  // Digital Floor Indicator - simplified for mobile
  digitalFloorIndicator: {
    position: "absolute",
    top: "5%",
    right: "5%",
    width: "80px",
    height: "100px",
    backgroundColor: "#050a14",
    color: "#0078ff",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "'Montserrat', sans-serif",
    zIndex: 15,
    boxShadow:
      "0 0 15px rgba(0, 123, 255, 0.3), inset 0 0 10px rgba(0, 123, 255, 0.1)",
    border: "1px solid #1a3a6c",
    overflow: "hidden",
  },
  floorIndicatorDisplay: {
    width: "90%",
    height: "90%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  floorDirectionIndicator: {
    position: "absolute",
    top: "1px",
    left: "1px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  directionArrowUp: {
    color: "#0078ff",
    fontSize: "0.9rem",
    textShadow: "0 0 5px rgba(0, 123, 255, 0.8)",
    opacity: 0.9,
  },
  floorNumberContainer: {
    position: "relative",
    marginBottom: "5px",
  },
  floorNumber: {
    fontSize: "3.5rem",
    fontWeight: "bold",
    color: "#0078ff",
    textShadow: "0 0 10px rgba(0, 123, 255, 0.7)",
    lineHeight: "1",
    letterSpacing: "-1px",
  },
  floorText: {
    fontSize: "0.7rem",
    fontWeight: "500",
    color: "#ffffff",
    letterSpacing: "1px",
    margin: "5px 0",
    textShadow: "0 0 5px rgba(255, 255, 255, 0.3)",
  },

  // Elevator Interior
  elevatorInterior: {
    position: "absolute",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  interiorWall: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom, #0f1725, #0a111d)",
    boxShadow: "inset 0 0 40px rgba(0,0,0,0.5)",
    overflow: "hidden",
  },

  // Digital display
  digitalDisplay: {
    position: "absolute",
    top: "35%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "70%",
    height: "12%",
    background: "#0a0e1a",
    borderRadius: "2px",
    border: "1px solid rgba(0, 123, 255, 0.3)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 0 15px rgba(0, 123, 255, 0.2)",
    zIndex: 3,
  },
  displayHeader: {
    fontSize: "0.7rem",
    color: "#ffffff",
    fontFamily: "'Montserrat', sans-serif",
    letterSpacing: "1px",
    fontWeight: "300",
  },
  displayContent: {
    fontSize: "1.2rem",
    color: "#0078ff",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: "700",
    letterSpacing: "1px",
    margin: "5px 0",
    textShadow: "0 0 8px rgba(0, 123, 255, 0.5)",
  },

  // Floor
  elevatorFloor: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "5%",
    background: "linear-gradient(to right, #0c1424, #0f1930, #0c1424)",
    boxShadow: "inset 0 1px 5px rgba(0,0,0,0.5)",
    overflow: "hidden",
  },

  // Control panel
  controlPanel: {
    position: "absolute",
    bottom: "15%",
    left: "5%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "8px",
    backgroundColor: "#0a1018",
    padding: "15px 12px",
    borderRadius: "4px",
    zIndex: 15,
    boxShadow: "0 5px 15px rgba(0,0,0,0.3), 0 0 8px rgba(0, 123, 255, 0.2)",
    border: "1px solid #0c1a2e",
  },
  panelScreen: {
    width: "100%",
    padding: "8px 5px",
    backgroundColor: "#050a12",
    color: "#0078ff",
    borderRadius: "3px",
    textAlign: "center",
    marginBottom: "8px",
    boxShadow: "inset 0 0 5px rgba(0, 123, 255, 0.2)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: "1px solid #0c1a2e",
  },
  screenText: {
    fontSize: "0.7rem",
    opacity: 0.7,
    marginBottom: "3px",
    fontFamily: "'Montserrat', sans-serif",
    letterSpacing: "1px",
  },
  screenNumber: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    textShadow: "0 0 5px rgba(0, 123, 255, 0.5)",
    fontFamily: "'Montserrat', sans-serif",
  },
  floorButton: {
    width: "35px",
    height: "35px",
    borderRadius: "3px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    color: "white",
    cursor: "pointer",
    boxShadow: "inset 0 0 5px rgba(0,0,0,0.5), 0 2px 5px rgba(0,0,0,0.3)",
    border: "1px solid #0c1a2e",
    transition: "background-color 0.3s ease",
    fontFamily: "'Montserrat', sans-serif",
  },

  // Door styling
  door: {
    position: "absolute",
    top: 0,
    height: "100%",
    width: "50%",
    background:
      "linear-gradient(135deg, #1a1a1a 0%, #0c0c0c 40%, #1a1a1a 60%, #0c0c0c 100%)",
    transition: "transform 1.5s cubic-bezier(0.65, 0, 0.35, 1)",
    zIndex: 10,
    boxShadow: "0 0 15px rgba(0,0,0,0.5)",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    borderBottom: "1px solid rgba(0,0,0,0.8)",
    overflow: "hidden",
  },
  leftDoor: {
    left: 0,
    borderRight: "1px solid rgba(255,255,255,0.07)",
  },
  rightDoor: {
    right: 0,
    borderLeft: "1px solid rgba(255,255,255,0.07)",
  },
  doorHandleRight: {
    position: "absolute",
    top: "50%",
    width: "4px",
    height: "60px",
    background: "linear-gradient(to right, #1a1a1a, #2a2a2a, #1a1a1a)",
    borderRadius: "3px",
    transform: "translateY(-50%)",
    left: "12px",
    boxShadow: "0 0 5px rgba(0,0,0,0.6), inset 0 0 3px rgba(255,255,255,0.1)",
    border: "1px solid rgba(0,0,0,0.3)",
  },
  doorHandleLeft: {
    position: "absolute",
    top: "50%",
    width: "4px",
    height: "60px",
    background: "linear-gradient(to right, #1a1a1a, #2a2a2a, #1a1a1a)",
    borderRadius: "3px",
    transform: "translateY(-50%)",
    right: "12px",
    boxShadow: "0 0 5px rgba(0,0,0,0.6), inset 0 0 3px rgba(255,255,255,0.1)",
    border: "1px solid rgba(0,0,0,0.3)",
  },

  // Elevator lighting
  elevatorLight: {
    position: "absolute",
    top: "8%",
    left: "50%",
    width: "150px",
    height: "150px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(0, 123, 255, 0.4) 0%, rgba(0, 123, 255, 0.1) 50%, rgba(0, 123, 255, 0) 70%)",
    transform: "translate(-50%, -30%)",
    opacity: 0.7,
    pointerEvents: "none",
    zIndex: 5,
    transition: "opacity 0.8s ease",
  },
};

export default MobileElevatorAnimation;
